/** E4 + Hotfix D18 · 全系統「功能模式 / 聊天模式」分段切換（基底元件·非 ee/）。
 *
 * 圓角膠囊容器內含兩顆按鈕（功能 / 聊天）；目前模式實心不可點，另一模式透明可點即切換。
 * 聊天模式不可用時整個不顯示（E4 useChatModeAvailable）。目前模式依路由判斷（/ee_ai_chat 起為聊天）。
 * 美化僅改外觀，切換行為不變（功能→/home·聊天→/ee_ai_chat）。
 *
 * variant（比照 LanguageSwitcher）：
 *   onPrimary — MainLayout 藍色漸層頂部（實心按鈕文字取 --header-bg-start·隨租戶色系）
 *   surface   — 聊天頁（走 MUI theme·深淺主題自適配）
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ForumIcon from '@mui/icons-material/Forum';

import { useChatModeAvailable } from '../hooks/useChatModeAvailable';
import { detectMobileEnv } from '../hooks/useIsMobile';

interface Props {
  variant?: 'onPrimary' | 'surface';
}

const ModeSwitcher: React.FC<Props> = ({ variant = 'surface' }) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const available = useChatModeAvailable();

  if (!available) return null;   // 只有功能模式 → 不顯示

  const current: 'function' | 'chat' = pathname.startsWith('/ee_ai_chat') ? 'chat' : 'function';
  // D20：手機的「功能模式」目標為行動版首頁 /ee_pwa_home；桌機維持 /home
  const functionHome = detectMobileEnv() ? '/ee_pwa_home' : '/home';
  const go = (mode: 'function' | 'chat') => {
    if (mode === current) return;   // 目前模式不動作
    nav(mode === 'chat' ? '/ee_ai_chat' : functionHome);
  };

  const onPrimary = variant === 'onPrimary';
  const containerSx = onPrimary
    ? { bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)' }
    : { bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' };

  const segSx = (active: boolean) => {
    if (active) {
      return onPrimary
        ? { bgcolor: '#fff', color: 'var(--header-bg-start)', fontWeight: 700 }
        : { bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 };
    }
    return onPrimary
      ? { color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }
      : { color: 'text.secondary', '&:hover': { bgcolor: 'action.selected' } };
  };

  const seg = (mode: 'function' | 'chat', icon: React.ReactNode, short: string, full: string) => {
    const active = current === mode;
    const btn = (
      <Box
        component="button"
        type="button"
        aria-pressed={active}
        aria-label={full}
        onClick={() => go(mode)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, border: 0, cursor: active ? 'default' : 'pointer',
          height: 28, px: compact ? 1 : 1.5, borderRadius: 999, font: 'inherit', fontSize: 13,
          lineHeight: 1, transition: 'background-color .15s, color .15s',
          '&:focus-visible': { outline: '2px solid', outlineColor: onPrimary ? '#fff' : 'primary.main', outlineOffset: 1 },
          ...segSx(active),
        }}
      >
        {icon}
        {!compact && <span>{short}</span>}
      </Box>
    );
    return compact ? <Tooltip key={mode} title={full}>{btn}</Tooltip> : <React.Fragment key={mode}>{btn}</React.Fragment>;
  };

  return (
    <Box
      role="group"
      aria-label={t('mode.switchLabel', '切換模式')}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, p: '3px', borderRadius: 999, height: 34, ...containerSx }}
    >
      {seg('function', <ViewModuleIcon sx={{ fontSize: 18 }} />, t('mode.functionShort', '功能'), t('mode.function', '功能模式'))}
      {seg('chat', <ForumIcon sx={{ fontSize: 18 }} />, t('mode.chatShort', '聊天'), t('mode.chat', '聊天模式'))}
    </Box>
  );
};

export default ModeSwitcher;
